import React from "react";
import { useRef } from "react";
import {
  FieldValues,
  useController,
  UseControllerProps,
} from "react-hook-form";
import { toastMessage } from "@/utils/toastHelper";
import { Button } from "@/uiComponents/components/ui/button";
import { Input } from "@/uiComponents/components/ui/input";
import { X } from "lucide-react";
import { FileType } from "@/enums/fileType";
import FilePreview from "./FilePreview";

interface BillPhotosInputProps<T extends FieldValues>
  extends UseControllerProps<T> {
  label?: string;
  className?: string;
  maxSizeMB?: number;
}

export const BillPhotosInput = <T extends FieldValues>({
  name,
  control,
  label = "Choose Photo or PDF",
  className = "",
  maxSizeMB = 8,
}: BillPhotosInputProps<T>) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const {
    field: { onChange, value = [] },
  } = useController({ name, control });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ?? [];
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    const allowedTypes = [
      FileType.JPG,
      FileType.JPEG,
      FileType.GIF,
      FileType.PNG,
      FileType.WEBP,
      FileType.SVG,
      FileType.PDF,
    ];

    const validFiles = Array.from(files).filter((file) => {
      if (!allowedTypes.includes(file.type as FileType)) {
        toastMessage("error", `File "${file.name}" is not an allowed format.`);
        toastMessage("info", "Only PDF and images are allowed.");
        return false;
      }
      if (file.size > maxSizeBytes) {
        toastMessage("error", `File "${file.name}" exceeds ${maxSizeMB}MB.`);
        return false;
      }
      return true;
    });

    const updatedFiles = [...value, ...validFiles];
    onChange(updatedFiles);
  };

  const removeFile = (index: number) => {
    const updatedFiles = value.filter((_, i) => i !== index);

    onChange(updatedFiles);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          onClick={() => fileRef.current?.click()}
          variant="outline"
          className="w-full border p-2 rounded-md text-left font-normal text-md flex items-center justify-between bg-white  hover:bg-white focus:ring-3 focus:shadow-lg focus:outline-none focus:ring-zinc-400 "
        >
          {label}
        </Button>
        <Input
          type="file"
          ref={fileRef}
          className="hidden"
          accept="image/*,application/pdf"
          multiple
          onChange={handleFileChange}
        />
      </div>

      {value?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-4">
          {value.map((file: File | { url: string }, idx: number) => {
            const src =
              //@ts-expect-error:skip
              file instanceof File ? URL.createObjectURL(file) : file.photo;
            //@ts-expect-error:skip
            const name = file instanceof File ? file.name : file.photo;
            const isPdf =
              file instanceof File
                ? file.type === "application/pdf" ||
                  file?.name?.toLowerCase().endsWith(".pdf")
                : //@ts-expect-error:skip
                  file?.photo?.toLowerCase().includes(".pdf");

            return (
              <div key={idx} className="relative">
                <button
                  onClick={() => removeFile(idx)}
                  type="button"
                  className="absolute top-0 right-0 p-1 bg-white rounded-full z-10"
                >
                  <X size={16} />
                </button>

                <div className="w-20 h-25 overflow-hidden rounded-lg shadow-md">
                  <FilePreview
                    src={src}
                    name={name}
                    isPdf={isPdf}
                    showName={true}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
